import PropTypes from "prop-types";
import React, { useState } from "react";
import { Alert, Button, CheckboxGroup, Modal, SelectField, TextField, Tooltip } from "@yoast/ui-library";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import { doRequest, prepareInternalRequest } from "shared-frontend/functions/api";
import * as styles from "./PersonalAccessTokensPageStyles.module.scss";

const EXPIRY_NEVER = "never";

const expiryOptions = [
	{ value: "7",          labelId: "personalAccessTokens.create.expires.7d",   defaultMessage: "7 days" },
	{ value: "30",         labelId: "personalAccessTokens.create.expires.30d",  defaultMessage: "30 days" },
	{ value: "90",         labelId: "personalAccessTokens.create.expires.90d",  defaultMessage: "90 days" },
	{ value: EXPIRY_NEVER, labelId: "personalAccessTokens.create.expires.never", defaultMessage: "No expiry" },
];

/**
 * Translates an expiry-dropdown value into an ISO timestamp.
 *
 * @param {string} value The selected dropdown value (number of days as string, or "never").
 *
 * @returns {string|null} ISO timestamp, or null when the token should not expire.
 */
function expiryValueToIsoString( value ) {
	if ( value === EXPIRY_NEVER ) {
		return null;
	}
	const days = Number.parseInt( value, 10 );
	const expiry = new Date();
	expiry.setDate( expiry.getDate() + days );
	return expiry.toISOString();
}

const messages = defineMessages( {
	title: {
		id: "personalAccessTokens.create.title",
		defaultMessage: "Create a personal access token",
	},
	ariaLabel: {
		id: "personalAccessTokens.create.ariaLabel",
		defaultMessage: "Create a personal access token",
	},
	nameLabel: {
		id: "personalAccessTokens.create.nameLabel",
		defaultMessage: "What is this token for?",
	},
	nameDescription: {
		id: "personalAccessTokens.create.nameDescription",
		defaultMessage: "A short, recognizable name so you can identify this token in the list later. For example: \"plugin release CI\" or \"laptop sync script\".",
	},
	expiresLabel: {
		id: "personalAccessTokens.create.expiresLabel",
		defaultMessage: "Expiration",
	},
	expiresDescription: {
		id: "personalAccessTokens.create.expiresDescription",
		defaultMessage: "Tokens that expire are safer. Pick the shortest period that still gets the job done.",
	},
	cancel: {
		id: "personalAccessTokens.create.cancel",
		defaultMessage: "Cancel",
	},
	submit: {
		id: "personalAccessTokens.create.submit",
		defaultMessage: "Create token",
	},
	error: {
		id: "personalAccessTokens.create.error",
		defaultMessage: "Could not create the token. Please try again.",
	},
	scopesLabel: {
		id: "personalAccessTokens.create.scopesLabel",
		defaultMessage: "Permissions",
	},
	scopesHint: {
		id: "personalAccessTokens.create.scopesHint",
		defaultMessage: "Pick the smallest set of permissions that you need.",
	},
	noScopesAvailable: {
		id: "personalAccessTokens.create.noScopesAvailable",
		defaultMessage: "No permissions are available yet.",
	},
	scopeHelpAriaLabel: {
		id: "personalAccessTokens.create.scopeHelpAriaLabel",
		defaultMessage: "More information about the {scope} permission",
	},
} );

/**
 * Modal for creating a new personal access token.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element} The modal.
 */
function CreatePersonalAccessTokenModal(
	{
		isOpen,
		onClose,
		onCreated,
		apiPath,
		intl,
		availableScopes = [],
	},
) {
	const [ name, setName ]                     = useState( "" );
	const [ expiryChoice, setExpiryChoice ]     = useState( "30" );
	const [ selectedScopes, setSelectedScopes ] = useState( [] );
	const [ isSubmitting, setSubmitting ]       = useState( false );
	const [ submitError, setSubmitError ]       = useState( null );

	const toggleScope = ( id ) => {
		setSelectedScopes( prev => prev.includes( id ) ? prev.filter( x => x !== id ) : [ ...prev, id ] );
	};

	const reset = () => {
		setName( "" );
		setExpiryChoice( "30" );
		setSelectedScopes( [] );
		setSubmitError( null );
		setSubmitting( false );
	};

	const handleClose = () => {
		reset();
		onClose();
	};

	const handleSubmit = async( event ) => {
		event.preventDefault();
		if ( ! name.trim() || selectedScopes.length === 0 || isSubmitting ) {
			return;
		}
		setSubmitting( true );
		setSubmitError( null );
		try {
			const payload = { name: name.trim(), scopes: selectedScopes };
			const expiresAt = expiryValueToIsoString( expiryChoice );
			if ( expiresAt ) {
				payload.expiresAt = expiresAt;
			}
			const request = prepareInternalRequest( apiPath, "POST", payload );
			const created = await doRequest( request );
			reset();
			onCreated( created );
		} catch ( e ) {
			setSubmitError( e );
			setSubmitting( false );
		}
	};

	const submitDisabled = isSubmitting || ! name.trim() || selectedScopes.length === 0;

	return (
		<Modal
			isOpen={ isOpen }
			onClose={ handleClose }
			aria-label={ intl.formatMessage( messages.ariaLabel ) }
		>
			<Modal.Panel>
				<form onSubmit={ handleSubmit }>
					<Modal.Title as="h2" size="3">
						<FormattedMessage { ...messages.title } />
					</Modal.Title>
					<div className={ styles.modalBody }>
						<TextField
							id="pat-name"
							name="name"
							type="text"
							label={ intl.formatMessage( messages.nameLabel ) }
							description={ intl.formatMessage( messages.nameDescription ) }
							value={ name }
							required={ true }
							maxLength={ 191 }
							onChange={ ( e ) => setName( e.target.value ) }
						/>
						<SelectField
							id="pat-expires"
							name="expiresAt"
							label={ intl.formatMessage( messages.expiresLabel ) }
							description={ intl.formatMessage( messages.expiresDescription ) }
							value={ expiryChoice }
							onChange={ setExpiryChoice }
							options={ expiryOptions.map( option => ( {
								value: option.value,
								label: intl.formatMessage( { id: option.labelId, defaultMessage: option.defaultMessage } ),
							} ) ) }
						/>
						<CheckboxGroup
							id="pat-scopes"
							label={ intl.formatMessage( messages.scopesLabel ) }
							description={ intl.formatMessage( messages.scopesHint ) }
						>
							{ availableScopes.length === 0
								? <p><FormattedMessage { ...messages.noScopesAvailable } /></p>
								: availableScopes.map( scope => (
									<div key={ scope.id } className={ styles.scopeRow }>
										<CheckboxGroup.Checkbox
											id={ `pat-scope-${ scope.id }` }
											name="scopes"
											value={ scope.id }
											label={ scope.label }
											checked={ selectedScopes.includes( scope.id ) }
											onChange={ () => toggleScope( scope.id ) }
										/>
										{ scope.description && (
											<span
												className={ styles.scopeHelp }
												tabIndex={ 0 }
												role="button"
												aria-label={ intl.formatMessage( messages.scopeHelpAriaLabel, { scope: scope.label } ) }
												aria-describedby={ `pat-scope-${ scope.id }-tooltip` }
											>
												<InformationCircleIcon
													className={ styles.scopeHelpIcon }
													aria-hidden="true"
												/>
												<Tooltip
													id={ `pat-scope-${ scope.id }-tooltip` }
													className={ styles.scopeTooltip }
													position="top"
												>
													{ scope.description }
												</Tooltip>
											</span>
										) }
									</div>
								) )
							}
						</CheckboxGroup>
						{ submitError && (
							<Alert variant="error">
								<FormattedMessage { ...messages.error } />
							</Alert>
						) }
					</div>
					<div className={ styles.modalActions }>
						<Button
							variant="secondary"
							onClick={ handleClose }
							disabled={ isSubmitting }
						>
							<FormattedMessage { ...messages.cancel } />
						</Button>
						<Button
							type="submit"
							variant="primary"
							isLoading={ isSubmitting }
							disabled={ submitDisabled }
						>
							<FormattedMessage { ...messages.submit } />
						</Button>
					</div>
				</form>
			</Modal.Panel>
		</Modal>
	);
}

CreatePersonalAccessTokenModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onCreated: PropTypes.func.isRequired,
	apiPath: PropTypes.string.isRequired,
	availableScopes: PropTypes.arrayOf( PropTypes.shape( {
		id: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		description: PropTypes.string,
	} ) ),
	intl: intlShape.isRequired,
};

export default injectIntl( CreatePersonalAccessTokenModal );
